import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  SimpleGrid,
  Switch,
  Text,
  Textarea,
  VStack
} from '@chakra-ui/react'

import { FormField } from '../../../../components/FormField'
import { Icon } from '../../../../components/Icon'
import { FormSelect } from '../../../../components/Select'
import { Tooltip } from '../../../../components/Tooltip'
import {
  EVENT_NAME_VARIABLE,
  EVENT_SURVEY_SCALE_MAX,
  EVENT_SURVEY_SCALE_MIN,
  TEventSurveyQuestionType
} from '../../../../services/Event/surveyTypes'
import {
  IEventSurveyQuestionDraft,
  MAX_QUESTIONS,
  newQuestion,
  QUESTION_TYPE_LABELS,
  SCALE_LABEL_MAX_LENGTH,
  SHORT_LABEL_MAX_LENGTH,
  STATEMENT_MAX_LENGTH
} from './eventSurveyForm'

interface IEventSurveyQuestionsEditorProps {
  questions: IEventSurveyQuestionDraft[]
  onChange: (questions: IEventSurveyQuestionDraft[]) => void
  /** Erros da API (ou da validação local) nas chaves do Laravel: `questions.0.statement`. */
  errors?: Record<string, string[]>
  /** Perguntas que já têm respostas: não saem da pesquisa nem mudam de tipo. */
  lockedIds?: number[]
  hasResponses?: boolean
  disabled?: boolean
}

/**
 * Editor das perguntas da pesquisa NPS do evento (ABRA-370), no molde do editor de campos de
 * obra dos concursos (`ArtFieldsEditor`): a ordem do array é a ordem do formulário no site.
 *
 * A pergunta de NPS (a de recomendação) é a que o relatório usa para o número principal, então
 * ela não sai da pesquisa nem deixa de ser de nota. As perguntas já respondidas também não: o
 * relatório perderia a coluna e as médias que já existem.
 */
export default function EventSurveyQuestionsEditor({
  questions,
  onChange,
  errors = {},
  lockedIds = [],
  hasResponses = false,
  disabled = false
}: IEventSurveyQuestionsEditorProps) {
  const fieldError = (key: string): string[] | undefined => errors[key]

  const isLocked = (question: IEventSurveyQuestionDraft) => question.id !== null && lockedIds.includes(question.id)

  const updateQuestion = (index: number, patch: Partial<IEventSurveyQuestionDraft>) => {
    onChange(questions.map((question, i) => (i === index ? { ...question, ...patch } : question)))
  }

  const removeQuestion = (index: number) => {
    onChange(questions.filter((_, i) => i !== index))
  }

  const moveQuestion = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= questions.length) return
    const next = [...questions]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  const addQuestion = (type: TEventSurveyQuestionType) => {
    onChange([...questions, newQuestion(type)])
  }

  const canAdd = !disabled && questions.length < MAX_QUESTIONS

  return (
    <Box>
      <FormField label="Perguntas" error={fieldError('questions')} mb={1}>
        <Text variant="caption" mb={3}>
          As perguntas aparecem no site nessa ordem; use as setas para reordenar. As notas vão sempre de{' '}
          {EVENT_SURVEY_SCALE_MIN} a {EVENT_SURVEY_SCALE_MAX}. Escreva <strong>{EVENT_NAME_VARIABLE}</strong> no
          enunciado para mostrar o nome do evento.
        </Text>
      </FormField>

      {hasResponses && (
        <Alert status="warning" rounded="lg" mb={3}>
          <AlertIcon />
          <AlertDescription fontSize="sm">
            Esta pesquisa já tem respostas. As perguntas respondidas não podem ser removidas nem mudar de tipo, e
            alterar o enunciado delas muda o sentido das respostas que já existem.
          </AlertDescription>
        </Alert>
      )}

      <VStack spacing={3} align="stretch">
        {questions.map((question, index) => {
          const locked = isLocked(question)
          const typeLocked = locked || question.is_nps
          const removeBlockedReason = question.is_nps
            ? 'A pergunta de NPS não pode ser removida'
            : locked
              ? 'Pergunta com respostas não pode ser removida'
              : null

          return (
            <Box
              key={question.clientKey}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              p={3}
              bg="gray.50"
            >
              <Flex align="center" justify="space-between" gap={2} mb={3}>
                <HStack spacing={2} flexWrap="wrap">
                  <Badge colorScheme="gray">{index + 1}</Badge>
                  <Badge colorScheme={question.type === 'scale' ? 'blue' : 'purple'}>
                    {QUESTION_TYPE_LABELS[question.type]}
                  </Badge>
                  {question.is_nps && (
                    <Tooltip label="É a pergunta de recomendação: dela sai o NPS do evento">
                      <Badge colorScheme="magenta" tabIndex={0} cursor="help">
                        NPS
                      </Badge>
                    </Tooltip>
                  )}
                  {locked && <Badge colorScheme="orange">Com respostas</Badge>}
                </HStack>

                <HStack spacing={0} flexShrink={0}>
                  <IconButton
                    aria-label="Mover pergunta para cima"
                    icon={<ChevronUpIcon boxSize={5} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => moveQuestion(index, -1)}
                    isDisabled={disabled || index === 0}
                  />
                  <IconButton
                    aria-label="Mover pergunta para baixo"
                    icon={<ChevronDownIcon boxSize={5} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => moveQuestion(index, 1)}
                    isDisabled={disabled || index === questions.length - 1}
                  />
                  <Tooltip label={removeBlockedReason ?? 'Remover pergunta'}>
                    {/* O Box segura o tooltip quando o botão está desabilitado: botão desabilitado
                        não dispara os eventos de mouse que abrem o balão. */}
                    <Box as="span" display="inline-flex">
                      <IconButton
                        aria-label="Remover pergunta"
                        icon={<Icon name="lni lni-trash-can" />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => removeQuestion(index)}
                        isDisabled={disabled || !!removeBlockedReason}
                      />
                    </Box>
                  </Tooltip>
                </HStack>
              </Flex>

              <VStack spacing={3} align="stretch">
                <FormField label="Enunciado" error={fieldError(`questions.${index}.statement`)} isRequired>
                  <Textarea
                    size="sm"
                    bg="white"
                    rows={2}
                    resize="vertical"
                    value={question.statement}
                    placeholder={`Ex: Em uma escala de 0 a 10, o quanto você indicaria o ${EVENT_NAME_VARIABLE} para um amigo?`}
                    maxLength={STATEMENT_MAX_LENGTH}
                    onChange={(e) => updateQuestion(index, { statement: e.target.value })}
                    isDisabled={disabled}
                  />
                </FormField>

                <Flex gap={3} direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'flex-start' }}>
                  <FormField
                    label="Rótulo no relatório"
                    flex="1"
                    helperText="Nome curto da coluna e do card de resultados."
                    error={fieldError(`questions.${index}.short_label`)}
                    isRequired
                  >
                    <Input
                      size="sm"
                      bg="white"
                      value={question.short_label}
                      placeholder="Ex: Recomendação"
                      maxLength={SHORT_LABEL_MAX_LENGTH}
                      onChange={(e) => updateQuestion(index, { short_label: e.target.value })}
                      isDisabled={disabled}
                    />
                  </FormField>

                  <FormField
                    label="Tipo"
                    flex="1"
                    helperText={
                      question.is_nps
                        ? 'A pergunta de NPS é sempre de nota.'
                        : locked
                          ? 'Já respondida: o tipo não pode mudar.'
                          : undefined
                    }
                    error={[
                      ...(fieldError(`questions.${index}.type`) ?? []),
                      ...(fieldError(`questions.${index}.is_nps`) ?? []),
                      ...(fieldError(`questions.${index}.id`) ?? [])
                    ]}
                  >
                    <FormSelect
                      size="sm"
                      bg="white"
                      value={question.type}
                      onChange={(value) => updateQuestion(index, { type: value as TEventSurveyQuestionType })}
                      options={(Object.keys(QUESTION_TYPE_LABELS) as TEventSurveyQuestionType[]).map((type) => ({
                        value: type,
                        label: QUESTION_TYPE_LABELS[type]
                      }))}
                      isDisabled={disabled || typeLocked}
                      hideEmptyOption
                    />
                  </FormField>

                  <FormField label="Obrigatória" htmlFor={`required-${question.clientKey}`} w="auto">
                    <Switch
                      id={`required-${question.clientKey}`}
                      mt={1}
                      isChecked={question.is_required}
                      onChange={(e) => updateQuestion(index, { is_required: e.target.checked })}
                      isDisabled={disabled}
                    />
                  </FormField>
                </Flex>

                {question.type === 'scale' && (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <FormField
                      label={`Legenda da nota ${EVENT_SURVEY_SCALE_MIN}`}
                      error={fieldError(`questions.${index}.min_label`)}
                    >
                      <Input
                        size="sm"
                        bg="white"
                        value={question.min_label}
                        placeholder="Ex: Não indicaria"
                        maxLength={SCALE_LABEL_MAX_LENGTH}
                        onChange={(e) => updateQuestion(index, { min_label: e.target.value })}
                        isDisabled={disabled}
                      />
                    </FormField>
                    <FormField
                      label={`Legenda da nota ${EVENT_SURVEY_SCALE_MAX}`}
                      error={fieldError(`questions.${index}.max_label`)}
                    >
                      <Input
                        size="sm"
                        bg="white"
                        value={question.max_label}
                        placeholder="Ex: Indicaria com certeza"
                        maxLength={SCALE_LABEL_MAX_LENGTH}
                        onChange={(e) => updateQuestion(index, { max_label: e.target.value })}
                        isDisabled={disabled}
                      />
                    </FormField>
                  </SimpleGrid>
                )}
              </VStack>
            </Box>
          )
        })}
      </VStack>

      <HStack mt={3} spacing={2} flexWrap="wrap">
        <Button
          size="sm"
          colorScheme="magenta"
          variant="outline"
          leftIcon={<Icon name="lni lni-plus" />}
          onClick={() => addQuestion('scale')}
          isDisabled={!canAdd}
        >
          Nota de 0 a 10
        </Button>
        <Button
          size="sm"
          colorScheme="magenta"
          variant="outline"
          leftIcon={<Icon name="lni lni-plus" />}
          onClick={() => addQuestion('text')}
          isDisabled={!canAdd}
        >
          Texto livre
        </Button>
        {questions.length >= MAX_QUESTIONS && (
          <Text variant="caption">Limite de {MAX_QUESTIONS} perguntas atingido.</Text>
        )}
      </HStack>
    </Box>
  )
}
